import React, { useEffect, useMemo } from "react";
import { Drawer, Box, Divider } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  donutChartConfigSelector,
  donutChartConfigDispatcher,
} from "@store/slices/donut-chart-config-slice";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { dataAvailabilityPreviewSelector } from "@store/slices/data-availability-preview-slice";
import { getEffectiveAvailabilityDate } from "@utils/data-availability-preview-utils";
import {
  useGetDatasetByIdQuery,
  useGetDatasetTimeBoundsQuery,
  useGetWeightConfigurationsQuery,
} from "@store/api/dataset-api";
import { buildDefaultDateGuardrails } from "@models/Dataset";
import { getPointInTimeUnitForPeriod } from "@utils/time-period-utils";
import { filterSubOnePercentSlices } from "@utils/donut-chart-utils";
import type { ViewerSelectableTimePeriod } from "@models/Dataset";
import type { WidgetRollup } from "@models/Rollup";
import BaseSection from "@components/charts/common/base-config/BaseSection";
import PointInTimeViewerOptionsSection from "@components/charts/PointInTimeViewerOptionsSection";
import RollingAverageSection from "@components/charts/RollingAverageSection";
import CategoryRollupsSection from "@components/charts/CategoryRollupsSection";
import type { BarOption } from "@components/charts/CategoryRollupBuilder";
import { useDonutChartData } from "./hooks/useDonutChartData";
import { useDonutChartHandlers } from "./hooks/useDonutChartHandlers";
import { useDonutChartDerivedState } from "./hooks/useDonutChartDerivedState";
import { useVisualizationBase } from "./hooks/useVisualizationBase";
import DrawerHeader from "./components/DrawerHeader";
import ConfigurationsSection from "./components/ConfigurationsSection";
import TimePeriodSection from "./components/TimePeriodSection";
import RollupShowAsSliceList from "./components/RollupShowAsSliceList";
import SliceCustomizationSection from "./components/SliceCustomizationSection";
import NDisplaySection from "./components/NDisplaySection";
import StatisticalTestingSection from "./components/StatisticalTestingSection";
import CenterValueSection from "./components/CenterValueSection";
import StylingSection from "./components/StylingSection";
import PreviewPanel from "./components/PreviewPanel";
import DrawerFooter from "./components/DrawerFooter";

const DonutChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetId, datasetName, activeFilters } =
    useAppSelector(donutChartConfigSelector);
  const { isDrawerOpen, editingWidgetId, dashboardId } =
    useAppSelector(chartDrawerSelector);
  const dataAvailabilityPreview = useAppSelector(dataAvailabilityPreviewSelector);

  const { data: datasetDetails } = useGetDatasetByIdQuery(datasetId as string, {
    skip: !datasetId,
  });

  const { data: timeBounds } = useGetDatasetTimeBoundsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  // Datasets don't always have dataAvailabilityDate set — fall back to the
  // actual max response date, same as TimePeriodSelection's "Latest Quarter
  // is..." helper text, so the drawer's title preview stays in sync with it.
  // Substituted with the active preview date when previewing this dashboard.
  const dataAvailabilityDate = getEffectiveAvailabilityDate(
    datasetDetails?.dataAvailabilityDate ?? timeBounds?.maxDate ?? null,
    dataAvailabilityPreview,
    dashboardId,
    datasetId,
  );

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  const pitGuardrails = useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);

  const currentPitUnit = useMemo(
    () => getPointInTimeUnitForPeriod(currentConfig.timePeriodSelection?.timePeriod ?? ""),
    [currentConfig.timePeriodSelection?.timePeriod],
  );

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updateWeight,
    updateDataSorting,
    updateDataLabelsPosition,
    updateLegendEnabled,
    updateColorPalette,
    updateSliceManualColor,
    setAllSliceManualColors,
    updateSliceLabelOverride,
    updateSlicePin,
    updateSliceCustomOrder,
    updateTruncateCategoryText,
    updatePercentageFormatting,
    updateSubOnePercentStyle,
    updateSliceValueFormat,
    updateMinSliceTextThreshold,
    updateForceShowAllValues,
    updateEmphasis,
    updateEmphasisDisplayFormat,
    updateEmphasisCustomText,
    updateViewerOptions,
    updateShowSliceN,
    updateShowChartN,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    addRollup,
    updateGroup,
    removeGroup,
  } = donutChartConfigDispatcher(dispatch);

  const { fetchChartData, isRefetchingData, weightsApplied } =
    useDonutChartData();

  const {
    localApplyFilters,
    isTogglingFilters,
    usePreviewDateFilter,
    handleFilterToggle,
    handlePreviewDateFilterToggle,
    handleWeightChange,
    handleTimePeriodChange,
    handleTimePeriodDatesChange,
    handleRollingAverageEnabledChange,
    handleRollingAverageWindowSizeChange,
    handleStatTestingChange,
    handleVisualizationBaseChange,
    handleSave,
    handleDiscard,
    isAllTimePeriod,
    isCreatingChart,
  } = useDonutChartHandlers({ fetchChartData });

  // When creating a new chart, default the weight to DATASET_DEFAULT if the
  // selected dataset has at least one weight configuration.
  useEffect(() => {
    if (
      editingWidgetId === "new-widget" &&
      (weightConfigData?.weightConfigurations?.length ?? 0) > 0 &&
      currentConfig.weight === "DISABLED"
    ) {
      updateWeight("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWidgetId, weightConfigData]);

  const {
    questions,
    getEmphasisOptions,
    isValidationError,
    getValidationTooltip,
    isAvailableData,
    headerTitle,
  } = useDonutChartDerivedState(dataAvailabilityDate);

  const { base: visualizationBase, isValid: isVisualizationBaseValid } =
    useVisualizationBase();

  const isDisabled = isRefetchingData || isTogglingFilters;

  const isViewerOptionsInvalid =
    currentPitUnit !== null &&
    currentConfig.viewerOptions.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalPeriods).length === 0 &&
    currentConfig.viewerOptions.chartPeriodAdditionalRollingAverages.length === 0;

  const canSave =
    !isValidationError &&
    !isAvailableData &&
    isVisualizationBaseValid &&
    !isViewerOptionsInvalid;
  const validationTooltip = isValidationError ? getValidationTooltip() : "";

  // Only list slices actually rendered on the pie — a Base/filter change can
  // push a slice under the Sub-1% threshold, and a shown-as-slice Rollup
  // absorbs its member slices into a single row (the chart's color pipeline
  // colors a Rollup by its first member's color, so that member stays the
  // storage key — only the label shown to the user changes).
  const sliceColorEntries = useMemo(() => {
    const rawSlices = currentConfig.series[0]?.data ?? [];
    const shownRollups = currentConfig.groups.filter(
      (g) => g.showAsSlice && g.sliceNames.length >= 2 && g.name.trim() && !g.isHidden,
    );
    const absorbedSliceNames = new Set(shownRollups.flatMap((g) => g.sliceNames));

    const individualEntries = rawSlices
      .filter((s) => !absorbedSliceNames.has(s.name))
      .map((s) => ({ name: s.name, y: s.y, fallbackColor: s.color || "#57bfe7" }));

    const rollupEntries = shownRollups.map((g) => {
      const firstMember = rawSlices.find((s) => s.name === g.sliceNames[0]);
      const y = g.sliceNames.reduce(
        (sum, n) => sum + (rawSlices.find((s) => s.name === n)?.y ?? 0),
        0,
      );
      return {
        name: g.sliceNames[0],
        y,
        fallbackColor: firstMember?.color || "#57bfe7",
        displayName: g.name,
        isRollup: true,
      };
    });

    return filterSubOnePercentSlices(
      [...individualEntries, ...rollupEntries],
      currentConfig.percentageFormatting,
      currentConfig.subOnePercentStyle,
    );
  }, [
    currentConfig.series,
    currentConfig.groups,
    currentConfig.percentageFormatting,
    currentConfig.subOnePercentStyle,
  ]);

  const handleManualColorChange = (sliceName: string, color: string | null) =>
    updateSliceManualColor({ sliceName, color });

  const handleApplyColorToAll = (color: string) =>
    setAllSliceManualColors({
      sliceNames: sliceColorEntries.map((s) => s.name),
      color,
    });

  // The rendered category list: individual slices not absorbed by a
  // shown-as-slice Rollup, plus one row per shown Rollup — matching exactly
  // what the pie itself draws (grouping + Sub-1% visibility).
  const renderedSliceEntries = useMemo(() => {
    const rawSlices = currentConfig.series[0]?.data ?? [];
    const useManualColors = currentConfig.colorPalette === "Custom (per-slice)";
    const resolveColor = (name: string, fallback: string) =>
      useManualColors && currentConfig.manualColors[name]
        ? currentConfig.manualColors[name]
        : fallback;

    const shownRollups = currentConfig.groups.filter(
      (g) => g.showAsSlice && g.sliceNames.length >= 2 && g.name.trim() && !g.isHidden,
    );
    const absorbedSliceNames = new Set(
      shownRollups.flatMap((g) => g.sliceNames),
    );

    const individualEntries = rawSlices
      .filter((s) => !absorbedSliceNames.has(s.name))
      .map((s) => ({
        name: s.name,
        y: s.y,
        color: resolveColor(s.name, s.color || "#57bfe7"),
        isRollup: false,
      }));

    const rollupEntries = shownRollups.map((g) => {
      const firstMember = rawSlices.find((s) => s.name === g.sliceNames[0]);
      const y = g.sliceNames.reduce(
        (sum, n) => sum + (rawSlices.find((s) => s.name === n)?.y ?? 0),
        0,
      );
      return {
        name: g.name,
        y,
        color: resolveColor(g.sliceNames[0], firstMember?.color || "#57bfe7"),
        isRollup: true,
      };
    });

    return filterSubOnePercentSlices(
      [...individualEntries, ...rollupEntries],
      currentConfig.percentageFormatting,
      currentConfig.subOnePercentStyle,
    );
  }, [
    currentConfig.series,
    currentConfig.groups,
    currentConfig.colorPalette,
    currentConfig.manualColors,
    currentConfig.percentageFormatting,
    currentConfig.subOnePercentStyle,
  ]);

  // Rollups — the shared CategoryRollupsSection/CategoryRollupBuilder drives
  // create/edit/delete/validation/preview so the interaction model matches
  // Bar Charts exactly; the donut-specific "Show as Slice" toggle is a
  // supplementary list underneath (see RollupShowAsSliceList).
  const rollupSliceOptions: BarOption[] = useMemo(
    () =>
      (currentConfig.series[0]?.data ?? []).map((slice) => {
        const displayLabel =
          currentConfig.sliceCustomization.labelOverrides[slice.name] ??
          slice.name;
        return {
          sourceLabel: slice.name,
          displayLabel,
          originalLabel: displayLabel !== slice.name ? slice.name : undefined,
        };
      }),
    [currentConfig.series, currentConfig.sliceCustomization],
  );

  const categoryRollupsAdapter: WidgetRollup[] = useMemo(
    () =>
      currentConfig.groups.map((g) => ({
        id: g.id,
        name: g.name,
        calculationType: "SUM_SELECTED" as const,
        sourceCategoryLabels: g.sliceNames,
      })),
    [currentConfig.groups],
  );

  const handleCreateRollup = (rollup: WidgetRollup) =>
    addRollup({
      id: rollup.id,
      name: rollup.name,
      sourceCategoryLabels: rollup.sourceCategoryLabels,
    });

  const handleUpdateRollup = (rollup: WidgetRollup) =>
    updateGroup({
      id: rollup.id,
      updates: { name: rollup.name ?? "", sliceNames: rollup.sourceCategoryLabels },
    });

  const handleShowAsSliceChange = (groupId: string, showAsSlice: boolean) =>
    updateGroup({ id: groupId, updates: { showAsSlice } });

  const dashboardDateOverride = useMemo(() => {
    if (!usePreviewDateFilter) return undefined;
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    if (dfStart && dfEnd) {
      return { startDate: dfStart, endDate: dfEnd };
    }
    return undefined;
  }, [usePreviewDateFilter]);

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{
        "& .MuiDrawer-paper": {
          width: "80vw",
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <DrawerHeader
          questionLabel={questions?.label}
          headerTitle={headerTitle}
          datasetName={datasetName}
          activeFiltersCount={activeFilters.length}
          localApplyFilters={localApplyFilters}
          isTogglingFilters={isTogglingFilters}
          isRefetchingData={isRefetchingData}
          onFilterToggle={handleFilterToggle}
          usePreviewDateFilter={usePreviewDateFilter}
          onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
        />

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <ConfigurationsSection
                weight={currentConfig.weight}
                weightsApplied={weightsApplied}
                isRefetchingData={isRefetchingData}
                dataSorting={currentConfig.dataSorting}
                onWeightChange={handleWeightChange}
                onDataSortingChange={updateDataSorting}
              />

              <Divider className="mx-[-24px]" />

              <BaseSection
                datasetId={datasetId}
                isDisabled={isDisabled}
                base={visualizationBase}
                onChange={handleVisualizationBaseChange}
              />

              <Divider className="mx-[-24px]" />

              <TimePeriodSection
                timePeriodSelection={currentConfig.timePeriodSelection}
                responsesMinDate={currentConfig.responsesMinDate}
                responsesMaxDate={currentConfig.responsesMaxDate}
                datasetId={datasetId}
                isDisabled={isDisabled}
                onTimePeriodChange={handleTimePeriodChange}
                onTimePeriodDatesChange={handleTimePeriodDatesChange}
              />

              {currentPitUnit && (
                <PointInTimeViewerOptionsSection
                  viewerOptions={currentConfig.viewerOptions}
                  chartTimePeriod={
                    currentConfig.timePeriodSelection
                      ?.timePeriod as ViewerSelectableTimePeriod
                  }
                  chartRollingWindowSize={
                    currentConfig.rollingAverage?.enabled
                      ? currentConfig.rollingAverage.windowSize
                      : 0
                  }
                  pitGuardrailUnits={pitGuardrails.units}
                  isDisabled={isDisabled}
                  onViewerOptionsChange={updateViewerOptions}
                  datasetId={datasetId}
                />
              )}

              <Divider className="mx-[-24px]" />

              <RollingAverageSection
                enabled={currentConfig.rollingAverage?.enabled || false}
                windowSize={currentConfig.rollingAverage?.windowSize || 2}
                timePeriod={currentConfig.timePeriodSelection?.timePeriod || "all_time"}
                totalAvailablePeriods={currentConfig.categories?.length}
                onEnabledChange={handleRollingAverageEnabledChange}
                onWindowSizeChange={handleRollingAverageWindowSizeChange}
                disabled={isDisabled}
              />

              <Divider className="mx-[-24px]" />

              <CategoryRollupsSection
                categoryRollups={categoryRollupsAdapter}
                barOptions={rollupSliceOptions}
                onCreateRollup={handleCreateRollup}
                onUpdateRollup={handleUpdateRollup}
                onDeleteRollup={removeGroup}
                title="Rollups"
                itemLabel="slices"
                nameLabel="Rollup Name"
              />

              <RollupShowAsSliceList
                groups={currentConfig.groups}
                onShowAsSliceChange={handleShowAsSliceChange}
              />

              <Divider className="mx-[-24px]" />

              <SliceCustomizationSection
                slices={renderedSliceEntries}
                labelOverrides={currentConfig.sliceCustomization.labelOverrides}
                customOrder={currentConfig.sliceCustomization.customOrder}
                pinnedStart={currentConfig.sliceCustomization.pinnedStart}
                pinnedEnd={currentConfig.sliceCustomization.pinnedEnd}
                dataSorting={currentConfig.dataSorting}
                colorPalette={currentConfig.colorPalette}
                onPinChange={(sliceName, position) =>
                  updateSlicePin({ sliceName, position })
                }
                onLabelChange={(sliceName, label) =>
                  updateSliceLabelOverride({ sliceName, label })
                }
                onResetLabel={(sliceName) =>
                  updateSliceLabelOverride({ sliceName, label: sliceName })
                }
                onCustomOrderChange={updateSliceCustomOrder}
              />

              <Divider className="mx-[-24px]" />

              <NDisplaySection
                showSliceN={currentConfig.showSliceN}
                showChartN={currentConfig.showChartN}
                onShowSliceNChange={updateShowSliceN}
                onShowChartNChange={updateShowChartN}
              />

              <Divider className="mx-[-24px]" />

              <StatisticalTestingSection
                statisticalTesting={currentConfig.statisticalTesting}
                isDisabled={isDisabled}
                isAllTimePeriod={isAllTimePeriod}
                rollingAverageEnabled={currentConfig.rollingAverage?.enabled || false}
                onStatTestingChange={handleStatTestingChange}
                updateStatTestingEnabled={updateStatTestingEnabled}
                updateStatTestType={updateStatTestType}
                updateSignificanceLevel={updateSignificanceLevel}
                updateMinimumSampleSize={updateMinimumSampleSize}
              />

              <Divider className="mx-[-24px]" />

              <CenterValueSection
                emphasis={currentConfig.emphasis}
                emphasisDisplayFormat={currentConfig.emphasisDisplayFormat}
                emphasisCustomText={currentConfig.emphasisCustomText}
                emphasisOptions={getEmphasisOptions()}
                onEmphasisChange={updateEmphasis}
                onEmphasisDisplayFormatChange={updateEmphasisDisplayFormat}
                onEmphasisCustomTextChange={updateEmphasisCustomText}
              />

              <Divider className="mx-[-24px]" />

              <StylingSection
                title={currentConfig.title}
                subtitle={currentConfig.subtitle}
                titleBold={currentConfig.titleBold}
                titleUnderline={currentConfig.titleUnderline}
                colorPalette={currentConfig.colorPalette}
                legendEnabled={currentConfig.legendEnabled}
                legendLockedBySliceN={currentConfig.showSliceN}
                percentageFormatting={currentConfig.percentageFormatting}
                subOnePercentStyle={currentConfig.subOnePercentStyle}
                dataLabelsPosition={currentConfig.dataLabelsPosition}
                sliceValueFormat={currentConfig.sliceValueFormat}
                minSliceTextThreshold={currentConfig.minSliceTextThreshold}
                forceShowAllValues={currentConfig.forceShowAllValues}
                truncateCategoryText={currentConfig.truncateCategoryText}
                sliceColorEntries={sliceColorEntries}
                manualColors={currentConfig.manualColors}
                onManualColorChange={handleManualColorChange}
                onApplyColorToAll={handleApplyColorToAll}
                onTitleChange={updateChartTitle}
                onSubtitleChange={updateChartSubtitle}
                onTitleBoldChange={updateTitleBold}
                onTitleUnderlineChange={updateTitleUnderline}
                onColorPaletteChange={updateColorPalette}
                onLegendEnabledChange={updateLegendEnabled}
                onPercentageFormattingChange={updatePercentageFormatting}
                onSubOnePercentStyleChange={updateSubOnePercentStyle}
                onDataLabelsPositionChange={updateDataLabelsPosition}
                onSliceValueFormatChange={updateSliceValueFormat}
                onMinSliceTextThresholdChange={updateMinSliceTextThreshold}
                onForceShowAllValuesChange={updateForceShowAllValues}
                onTruncateCategoryTextChange={updateTruncateCategoryText}
              />
            </Box>
          </Box>

          <PreviewPanel
            isRefetchingData={isRefetchingData}
            isAvailableData={isAvailableData}
            seriesName={currentConfig.series[0]?.name}
            visualizationBase={visualizationBase}
            currentConfig={currentConfig}
            questions={questions}
            dataAvailabilityDate={dataAvailabilityDate}
            dashboardDateOverride={dashboardDateOverride}
            weightsApplied={weightsApplied}
          />
        </Box>

        <DrawerFooter
          isCreatingChart={isCreatingChart}
          canSave={canSave}
          isRefetchingData={isRefetchingData}
          validationTooltip={validationTooltip}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </Box>
    </Drawer>
  );
};

export default DonutChartConfigDrawer;
